/* ==========================================================================
   FILE: modules/auth/utils/validation.js
   IAMONEAI - Authentication Validation Utilities
   Validation functions for auth forms
   ========================================================================== */

// ==========================================================================
// IIN VALIDATION
// ==========================================================================

/**
 * Format IIN with dashes (XXXX-XXXX-XXXX-XXXX)
 * @param {string} value - Raw input
 * @returns {string} Formatted IIN
 */
export function formatIIN(value) {
  // Remove all non-alphanumeric characters
  const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  
  // Add dashes every 4 characters
  const parts = [];
  for (let i = 0; i < cleaned.length && i < 16; i += 4) {
    parts.push(cleaned.slice(i, i + 4));
  }
  
  return parts.join('-');
}

/**
 * Validate IIN format
 * @param {string} iin - IIN to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateIIN(iin) {
  if (!iin) {
    return { valid: false, error: 'IIN is required' };
  }

  // Remove dashes for validation
  const cleaned = iin.replace(/-/g, '');

  if (cleaned.length !== 16) {
    return { valid: false, error: 'IIN must be 16 characters (XXXX-XXXX-XXXX-XXXX)' };
  }

  // Check format: should be alphanumeric
  if (!/^[A-Z0-9]+$/.test(cleaned)) {
    return { valid: false, error: 'IIN can only contain letters and numbers' };
  }

  // Check first 4 characters (year + prefix)
  const prefix = cleaned.slice(0, 4);
  if (!/^[0-9]{2}[A-Z]{2}$/.test(prefix)) {
    return { valid: false, error: 'Invalid IIN format' };
  }

  return { valid: true };
}

/**
 * Mask IIN for display (show first and last 4 chars)
 * @param {string} iin - Full IIN
 * @returns {string} Masked IIN
 */
export function maskIIN(iin) {
  if (!iin || iin.length < 8) return iin;
  const cleaned = iin.replace(/-/g, '');
  return `${cleaned.slice(0, 4)}-****-****-${cleaned.slice(-4)}`;
}

// ==========================================================================
// EMAIL VALIDATION
// ==========================================================================

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateEmail(email) {
  if (!email) {
    return { valid: false, error: 'Email is required' };
  }

  const trimmed = email.trim().toLowerCase();

  // Basic format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }

  // Check for common typos
  const commonTypos = {
    'gmial.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gamil.com': 'gmail.com',
    'gnail.com': 'gmail.com',
    'hotmai.com': 'hotmail.com',
    'hotmal.com': 'hotmail.com',
    'yaho.com': 'yahoo.com',
    'yahooo.com': 'yahoo.com',
    'outloo.com': 'outlook.com',
  };

  const domain = trimmed.split('@')[1];
  if (commonTypos[domain]) {
    return { 
      valid: false, 
      error: `Did you mean ${trimmed.replace(domain, commonTypos[domain])}?` 
    };
  }

  return { valid: true };
}

/**
 * List of known disposable email domains
 */
const DISPOSABLE_DOMAINS = [
  '10minutemail.com',
  'tempmail.com',
  'temp-mail.org',
  'guerrillamail.com',
  'throwaway.email',
  'mailinator.com',
  'yopmail.com',
  'getairmail.com',
  'fakeinbox.com',
  'trashmail.com',
  'mailnesia.com',
  'tempail.com',
  'dispostable.com',
  'mintemail.com',
  'tempr.email',
  'throwawaymail.com',
  'getnada.com',
  'emailondeck.com',
  'mohmal.com',
  'tempinbox.com',
  'burnermail.io',
  'sharklasers.com',
  'spamgourmet.com',
  'maildrop.cc',
  'mailsac.com',
  'inboxalias.com',
  'discard.email',
  'spambox.us',
  'mytemp.email',
  'temp.email',
  'tempmailaddress.com',
  '20minutemail.com',
  'fakemailgenerator.com',
  'emailfake.com',
  'generator.email',
  'luxusmail.org',
  'crazymailing.com',
  'mailcatch.com',
  // Add more as needed
];

/**
 * Check if email is from a disposable provider
 * @param {string} email - Email to check
 * @returns {boolean}
 */
export function isDisposableEmail(email) {
  if (!email) return false;
  
  const domain = email.trim().toLowerCase().split('@')[1];
  if (!domain) return false;

  // Check exact match
  if (DISPOSABLE_DOMAINS.includes(domain)) {
    return true;
  }

  // Check for subdomains of disposable domains
  for (const disposable of DISPOSABLE_DOMAINS) {
    if (domain.endsWith(`.${disposable}`)) {
      return true;
    }
  }

  return false;
}

// ==========================================================================
// NAME VALIDATION
// ==========================================================================

/**
 * Validate name (first or last)
 * @param {string} name - Name to validate
 * @param {string} fieldName - Field name for error message
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateName(name, fieldName = 'Name') {
  if (!name) {
    return { valid: false, error: `${fieldName} is required` };
  }

  const trimmed = name.trim();

  if (trimmed.length < 2) {
    return { valid: false, error: `${fieldName} must be at least 2 characters` };
  }

  if (trimmed.length > 50) {
    return { valid: false, error: `${fieldName} must be less than 50 characters` };
  }

  // Allow letters, spaces, hyphens, and apostrophes (for names like O'Brien, Mary-Jane)
  if (!/^[a-zA-Z\s\-']+$/.test(trimmed)) {
    return { valid: false, error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
  }

  return { valid: true };
}

// ==========================================================================
// BIRTH DATE VALIDATION
// ==========================================================================

/**
 * Validate birth date
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateBirthDate(dateString) {
  if (!dateString) {
    return { valid: false, error: 'Birth date is required' };
  }

  const date = new Date(dateString);
  const now = new Date();

  // Check if valid date
  if (isNaN(date.getTime())) {
    return { valid: false, error: 'Please enter a valid date' };
  }

  // Check not in future
  if (date > now) {
    return { valid: false, error: 'Birth date cannot be in the future' };
  }

  // Check minimum age (13 years)
  const minAgeDate = new Date();
  minAgeDate.setFullYear(minAgeDate.getFullYear() - 13);
  if (date > minAgeDate) {
    return { valid: false, error: 'You must be at least 13 years old' };
  }

  // Check maximum age (150 years - reasonable limit)
  const maxAgeDate = new Date();
  maxAgeDate.setFullYear(maxAgeDate.getFullYear() - 150);
  if (date < maxAgeDate) {
    return { valid: false, error: 'Please enter a valid birth date' };
  }

  return { valid: true };
}

/**
 * Calculate age from birth date
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {number} Age in years
 */
export function calculateAge(dateString) {
  const birthDate = new Date(dateString);
  const now = new Date();
  
  let age = now.getFullYear() - birthDate.getFullYear();
  const monthDiff = now.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
}

// ==========================================================================
// WALLET VALIDATION
// ==========================================================================

/**
 * Validate Ethereum wallet address
 * @param {string} wallet - Wallet address to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateWallet(wallet) {
  if (!wallet) {
    // Wallet is optional
    return { valid: true };
  }

  const trimmed = wallet.trim();

  // Basic Ethereum address format
  if (!/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
    return { valid: false, error: 'Please enter a valid Ethereum wallet address (0x...)' };
  }

  return { valid: true };
}

/**
 * Format wallet address for display (show first 6 and last 4)
 * @param {string} wallet - Full wallet address
 * @returns {string} Shortened address
 */
export function shortenWallet(wallet) {
  if (!wallet || wallet.length < 12) return wallet;
  return `${wallet.slice(0, 6)}...${wallet.slice(-4)}`;
}

// ==========================================================================
// VERIFICATION CODE VALIDATION
// ==========================================================================

/**
 * Validate verification code
 * @param {string} code - 6-digit code
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateVerificationCode(code) {
  if (!code) {
    return { valid: false, error: 'Verification code is required' };
  }

  const cleaned = code.replace(/\D/g, '');

  if (cleaned.length !== 6) {
    return { valid: false, error: 'Please enter the 6-digit code' };
  }

  return { valid: true };
}

// ==========================================================================
// PASSWORD VALIDATION (Future use)
// ==========================================================================

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {{ valid: boolean, error?: string, strength: 'weak' | 'medium' | 'strong' }}
 */
export function validatePassword(password) {
  if (!password) {
    return { valid: false, error: 'Password is required', strength: 'weak' };
  }

  if (password.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters', strength: 'weak' };
  }

  let strength = 'weak';
  let score = 0;

  // Length bonus
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;

  // Character type bonuses
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score >= 5) strength = 'strong';
  else if (score >= 3) strength = 'medium';

  // Common password check
  const commonPasswords = ['password', '12345678', 'qwerty', 'letmein', 'welcome'];
  if (commonPasswords.includes(password.toLowerCase())) {
    return { valid: false, error: 'Please choose a stronger password', strength: 'weak' };
  }

  return { valid: true, strength };
}

// ==========================================================================
// FORM VALIDATION HELPER
// ==========================================================================

/**
 * Validate multiple fields at once
 * @param {Object} fields - Object with field values
 * @param {Object} validators - Object with validator functions
 * @returns {{ valid: boolean, errors: Object }}
 */
export function validateForm(fields, validators) {
  const errors = {};
  let valid = true;

  for (const [field, validator] of Object.entries(validators)) {
    const result = validator(fields[field]);
    if (!result.valid) {
      errors[field] = result.error;
      valid = false;
    }
  }

  return { valid, errors };
}